import React, { useState, useEffect } from 'react';

interface SESMetrics {
    sends: number;
    deliveries: number;
    opens: number;
    clicks: number;
    bounces: number;
    complaints: number;
    rejects: number;
    deliveryRate: number;
    openRate: number;
    clickRate: number;
    bounceRate: number;
    complaintRate: number;
}

interface SESStatistics {
    last24Hours: SESMetrics;
    last7Days: SESMetrics;
    last30Days: SESMetrics;
    allTime: SESMetrics;
}

interface SESQuotaInfo {
    maxSendRate: number; // emails per second
    max24HourSend: number; // emails per 24 hours
    sentLast24Hours: number;
    remainingQuota: number;
}

const EmailMarketingDashboard: React.FC = () => {
    const [activeTab, setActiveTab] = useState<'overview' | 'stats' | 'health' | 'quota'>('overview');
    const [statistics, setStatistics] = useState<SESStatistics | null>(null);
    const [quotaInfo, setQuotaInfo] = useState<SESQuotaInfo | null>(null);
    const [isLoading, setIsLoading] = useState(true);
    const [timeframe, setTimeframe] = useState<'24h' | '7d' | '30d'>('24h');
    const [liveStats, setLiveStats] = useState<any>(null);
    const [liveTimeRange, setLiveTimeRange] = useState<'15min' | 'hour' | '4hours' | 'day'>('hour');

    useEffect(() => {
        loadSESData();
    }, []);


    const loadLiveStats = async () => {
        try {
            const token = localStorage.getItem('accessToken');
            const campaignApiUrl = 'https://52hauxiohb4m6lg54pn6itxkfi0rhvml.lambda-url.us-east-1.on.aws';

            const response = await fetch(`${campaignApiUrl}/delivery/realtime?range=${liveTimeRange}`, {
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                }
            });

            if (response.ok) {
                const result = await response.json();
                if (result.success) {
                    setLiveStats(result);
                    console.log('✅ Live stats refreshed:', result.stats);
                }
            }
        } catch (error) {
            console.error('❌ Error loading live stats:', error);
        }
    };

    const loadSESData = async () => {
        setIsLoading(true);
        try {
            // Fetch real SES statistics from the dedicated stats API
            const token = localStorage.getItem('accessToken');
            const statsApiUrl = 'https://cb24b6fbeln264x54q3mwsya3q0zvbit.lambda-url.us-east-1.on.aws/';

            const response = await fetch(statsApiUrl, {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                }
            });

            if (!response.ok) {
                throw new Error(`Failed to fetch SES statistics: ${response.status}`);
            }

            const result = await response.json();

            if (result.success && result.data) {
                console.log('✅ Real SES data loaded successfully:', result.data.statistics.last24Hours);
                setStatistics(result.data.statistics);
                setQuotaInfo(result.data.quota);
            } else {
                // Don't show dummy data if auth fails - show error instead
                console.error('⚠️ Failed to load SES data:', result.message || result.error);
                setStatistics(null);
                setQuotaInfo(null);
            }
        } catch (error) {
            console.error('❌ Error loading SES data:', error);
            // Don't show dummy data on error - show error state
            setStatistics(null);
            setQuotaInfo(null);
        } finally {
            setIsLoading(false);
        }
    };

    const generateDemoStatistics = (): SESStatistics => {
        return {
            last24Hours: {
                sends: 1247,
                deliveries: 1198,
                opens: 478,
                clicks: 145,
                bounces: 32,
                complaints: 3,
                rejects: 17,
                deliveryRate: 96.1,
                openRate: 39.9,
                clickRate: 12.1,
                bounceRate: 2.7,
                complaintRate: 0.3
            },
            last7Days: {
                sends: 8934,
                deliveries: 8621,
                opens: 3512,
                clicks: 1087,
                bounces: 234,
                complaints: 18,
                rejects: 97,
                deliveryRate: 96.5,
                openRate: 40.7,
                clickRate: 12.6,
                bounceRate: 2.7,
                complaintRate: 0.2
            },
            last30Days: {
                sends: 42178,
                deliveries: 40789,
                opens: 16823,
                clicks: 5198,
                bounces: 1012,
                complaints: 89,
                rejects: 468,
                deliveryRate: 96.7,
                openRate: 41.2,
                clickRate: 12.7,
                bounceRate: 2.5,
                complaintRate: 0.2
            },
            allTime: {
                sends: 587342,
                deliveries: 568129,
                opens: 234176,
                clicks: 72456,
                bounces: 14298,
                complaints: 1234,
                rejects: 6789,
                deliveryRate: 96.7,
                openRate: 41.2,
                clickRate: 12.7,
                bounceRate: 2.4,
                complaintRate: 0.2
            }
        };
    };

    const generateDemoQuota = (): SESQuotaInfo => {
        return {
            maxSendRate: 14, // emails per second
            max24HourSend: 50000, // emails per 24 hours
            sentLast24Hours: 1247,
            remainingQuota: 48753
        };
    };

    const getCurrentMetrics = (): SESMetrics | null => {
        if (!statistics) return null;
        switch (timeframe) {
            case '24h':
                return statistics.last24Hours;
            case '7d':
                return statistics.last7Days;
            case '30d':
                return statistics.last30Days;
            default:
                return statistics.last24Hours;
        }
    };

    const formatNumber = (num: number): string => {
        return num.toLocaleString();
    };

    const getHealthStatus = (): { status: 'excellent' | 'good' | 'warning' | 'critical', message: string } => {
        const metrics = statistics?.last24Hours;
        if (!metrics) return { status: 'good', message: 'Loading...' };

        if (metrics.bounceRate > 5 || metrics.complaintRate > 0.5) {
            return { status: 'critical', message: 'Critical: High bounce or complaint rate' };
        } else if (metrics.bounceRate > 3 || metrics.complaintRate > 0.3) {
            return { status: 'warning', message: 'Warning: Elevated bounce or complaint rate' };
        } else if (metrics.deliveryRate > 95 && metrics.bounceRate < 2) {
            return { status: 'excellent', message: 'Excellent: All metrics within optimal range' };
        } else {
            return { status: 'good', message: 'Good: Normal email deliverability' };
        }
    };

    if (isLoading) {
        return (
            <div className="email-marketing-dashboard">
                <div className="loading-spinner">Loading SES statistics...</div>
            </div>
        );
    }

    // Show error state if no data available
    if (!statistics || !quotaInfo) {
        return (
            <div className="email-marketing-dashboard">
                <div className="dashboard-header">
                    <div>
                        <h1>📧 Email Marketing Dashboard</h1>
                        <p className="header-subtitle">AWS SES Statistics & Performance Metrics</p>
                    </div>
                </div>
                <div className="error-state" style={{
                    padding: '60px 20px',
                    textAlign: 'center',
                    backgroundColor: '#fff',
                    borderRadius: '8px',
                    border: '2px dashed #e0e0e0'
                }}>
                    <div style={{ fontSize: '64px', marginBottom: '20px' }}>📊</div>
                    <h2 style={{ color: '#666', marginBottom: '10px' }}>No SES Data Available</h2>
                    <p style={{ color: '#999', marginBottom: '20px' }}>
                        Unable to load AWS SES statistics. This could be due to:
                    </p>
                    <ul style={{
                        listStyle: 'none',
                        padding: 0,
                        margin: '0 auto 30px',
                        maxWidth: '400px',
                        textAlign: 'left',
                        color: '#666'
                    }}>
                        <li style={{ marginBottom: '8px' }}>• Authentication required (please log in as system admin)</li>
                        <li style={{ marginBottom: '8px' }}>• No data collected yet</li>
                        <li style={{ marginBottom: '8px' }}>• Network or API error</li>
                    </ul>
                    <button className="btn-refresh" onClick={loadSESData} style={{
                        padding: '12px 24px',
                        fontSize: '16px',
                        cursor: 'pointer'
                    }}>
                        🔄 Retry
                    </button>
                </div>
            </div>
        );
    }

    const currentMetrics = getCurrentMetrics();
    const healthStatus = getHealthStatus();

    return (
        <div className="email-marketing-dashboard">
            {/* Header */}
            <div className="dashboard-header">
                <div>
                    <h1>📧 Email Marketing Dashboard</h1>
                    <p className="header-subtitle">AWS SES Statistics & Performance Metrics</p>
                </div>
                <div className="header-actions">
                    <select
                        value={timeframe}
                        onChange={(e) => setTimeframe(e.target.value as any)}
                        className="timeframe-selector"
                    >
                        <option value="24h">Last 24 Hours</option>
                        <option value="7d">Last 7 Days</option>
                        <option value="30d">Last 30 Days</option>
                    </select>
                    <button className="btn-refresh" onClick={loadSESData}>
                        🔄 Refresh
                    </button>
                </div>
            </div>

            {/* Health Status Banner */}
            <div className={`health-banner health-${healthStatus.status}`}>
                <span className="health-icon">
                    {healthStatus.status === 'excellent' && '✅'}
                    {healthStatus.status === 'good' && '👍'}
                    {healthStatus.status === 'warning' && '⚠️'}
                    {healthStatus.status === 'critical' && '🚨'}
                </span>
                <span className="health-message">{healthStatus.message}</span>
            </div>

            {/* Tabs */}
            <div className="dashboard-tabs">
                <button
                    className={`tab ${activeTab === 'overview' ? 'active' : ''}`}
                    onClick={() => setActiveTab('overview')}
                >
                    📊 Overview
                </button>
                <button
                    className={`tab ${activeTab === 'stats' ? 'active' : ''}`}
                    onClick={() => setActiveTab('stats')}
                >
                    📈 Detailed Stats
                </button>
                <button
                    className={`tab ${activeTab === 'health' ? 'active' : ''}`}
                    onClick={() => setActiveTab('health')}
                >
                    ❤️ Health Metrics
                </button>
                <button
                    className={`tab ${activeTab === 'quota' ? 'active' : ''}`}
                    onClick={() => setActiveTab('quota')}
                >
                    📦 Quota & Limits
                </button>
            </div>

            {/* Overview Tab */}
            {activeTab === 'overview' && currentMetrics && (
                <div className="tab-content">
                    <div className="metrics-grid">
                        <div className="metric-card primary-card">
                            <div className="metric-icon">📤</div>
                            <div className="metric-content">
                                <h3>Total Sends</h3>
                                <p className="metric-value">{formatNumber(currentMetrics.sends)}</p>
                                <span className="metric-label">Emails sent</span>
                            </div>
                        </div>

                        <div className="metric-card success-card">
                            <div className="metric-icon">✅</div>
                            <div className="metric-content">
                                <h3>Deliveries</h3>
                                <p className="metric-value">{formatNumber(currentMetrics.deliveries)}</p>
                                <span className="metric-label">{currentMetrics.deliveryRate}% delivery rate</span>
                            </div>
                        </div>

                        <div className="metric-card info-card">
                            <div className="metric-icon">👀</div>
                            <div className="metric-content">
                                <h3>Opens</h3>
                                <p className="metric-value">{formatNumber(currentMetrics.opens)}</p>
                                <span className="metric-label">{currentMetrics.openRate}% open rate</span>
                            </div>
                        </div>

                        <div className="metric-card info-card">
                            <div className="metric-icon">🖱️</div>
                            <div className="metric-content">
                                <h3>Clicks</h3>
                                <p className="metric-value">{formatNumber(currentMetrics.clicks)}</p>
                                <span className="metric-label">{currentMetrics.clickRate}% click rate</span>
                            </div>
                        </div>

                        <div className="metric-card warning-card">
                            <div className="metric-icon">↩️</div>
                            <div className="metric-content">
                                <h3>Bounces</h3>
                                <p className="metric-value">{formatNumber(currentMetrics.bounces)}</p>
                                <span className="metric-label">{currentMetrics.bounceRate}% bounce rate</span>
                            </div>
                        </div>

                        <div className="metric-card danger-card">
                            <div className="metric-icon">⚠️</div>
                            <div className="metric-content">
                                <h3>Complaints</h3>
                                <p className="metric-value">{formatNumber(currentMetrics.complaints)}</p>
                                <span className="metric-label">{currentMetrics.complaintRate}% complaint rate</span>
                            </div>
                        </div>
                    </div>

                    {/* Performance Chart */}
                    <div className="performance-section">
                        <h2>Email Performance Metrics</h2>
                        <div className="performance-bars">
                            <div className="perf-bar-item">
                                <div className="perf-bar-header">
                                    <span>Delivery Rate</span>
                                    <strong className="text-success">{currentMetrics.deliveryRate}%</strong>
                                </div>
                                <div className="perf-bar-container">
                                    <div
                                        className="perf-bar-fill fill-success"
                                        style={{ width: `${currentMetrics.deliveryRate}%` }}
                                    />
                                </div>
                                <span className="perf-bar-note">Target: &gt;95%</span>
                            </div>

                            <div className="perf-bar-item">
                                <div className="perf-bar-header">
                                    <span>Open Rate</span>
                                    <strong className="text-info">{currentMetrics.openRate}%</strong>
                                </div>
                                <div className="perf-bar-container">
                                    <div
                                        className="perf-bar-fill fill-info"
                                        style={{ width: `${currentMetrics.openRate}%` }}
                                    />
                                </div>
                                <span className="perf-bar-note">Industry avg: ~20%</span>
                            </div>

                            <div className="perf-bar-item">
                                <div className="perf-bar-header">
                                    <span>Click Rate</span>
                                    <strong className="text-info">{currentMetrics.clickRate}%</strong>
                                </div>
                                <div className="perf-bar-container">
                                    <div
                                        className="perf-bar-fill fill-info"
                                        style={{ width: `${currentMetrics.clickRate * 3}%` }}
                                    />
                                </div>
                                <span className="perf-bar-note">Industry avg: ~2.5%</span>
                            </div>

                            <div className="perf-bar-item">
                                <div className="perf-bar-header">
                                    <span>Bounce Rate</span>
                                    <strong className={currentMetrics.bounceRate > 3 ? 'text-warning' : 'text-success'}>
                                        {currentMetrics.bounceRate}%
                                    </strong>
                                </div>
                                <div className="perf-bar-container">
                                    <div
                                        className={`perf-bar-fill ${currentMetrics.bounceRate > 3 ? 'fill-warning' : 'fill-success'}`}
                                        style={{ width: `${currentMetrics.bounceRate * 10}%` }}
                                    />
                                </div>
                                <span className="perf-bar-note">Target: &lt;2%</span>
                            </div>

                            <div className="perf-bar-item">
                                <div className="perf-bar-header">
                                    <span>Complaint Rate</span>
                                    <strong className={currentMetrics.complaintRate > 0.3 ? 'text-danger' : 'text-success'}>
                                        {currentMetrics.complaintRate}%
                                    </strong>
                                </div>
                                <div className="perf-bar-container">
                                    <div
                                        className={`perf-bar-fill ${currentMetrics.complaintRate > 0.3 ? 'fill-danger' : 'fill-success'}`}
                                        style={{ width: `${currentMetrics.complaintRate * 100}%` }}
                                    />
                                </div>
                                <span className="perf-bar-note">Target: &lt;0.1%</span>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Detailed Stats Tab */}
            {activeTab === 'stats' && statistics && (
                <div className="tab-content">
                    <div className="stats-comparison">
                        <h2>Historical Comparison</h2>
                        <table className="stats-table">
                            <thead>
                                <tr>
                                    <th>Metric</th>
                                    <th>Last 24 Hours</th>
                                    <th>Last 7 Days</th>
                                    <th>Last 30 Days</th>
                                    <th>All Time</th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr>
                                    <td>Sends</td>
                                    <td>{formatNumber(statistics.last24Hours.sends)}</td>
                                    <td>{formatNumber(statistics.last7Days.sends)}</td>
                                    <td>{formatNumber(statistics.last30Days.sends)}</td>
                                    <td>{formatNumber(statistics.allTime.sends)}</td>
                                </tr>
                                <tr>
                                    <td>Deliveries</td>
                                    <td>{formatNumber(statistics.last24Hours.deliveries)}</td>
                                    <td>{formatNumber(statistics.last7Days.deliveries)}</td>
                                    <td>{formatNumber(statistics.last30Days.deliveries)}</td>
                                    <td>{formatNumber(statistics.allTime.deliveries)}</td>
                                </tr>
                                <tr>
                                    <td>Opens</td>
                                    <td>{formatNumber(statistics.last24Hours.opens)}</td>
                                    <td>{formatNumber(statistics.last7Days.opens)}</td>
                                    <td>{formatNumber(statistics.last30Days.opens)}</td>
                                    <td>{formatNumber(statistics.allTime.opens)}</td>
                                </tr>
                                <tr>
                                    <td>Clicks</td>
                                    <td>{formatNumber(statistics.last24Hours.clicks)}</td>
                                    <td>{formatNumber(statistics.last7Days.clicks)}</td>
                                    <td>{formatNumber(statistics.last30Days.clicks)}</td>
                                    <td>{formatNumber(statistics.allTime.clicks)}</td>
                                </tr>
                                <tr>
                                    <td>Bounces</td>
                                    <td>{formatNumber(statistics.last24Hours.bounces)}</td>
                                    <td>{formatNumber(statistics.last7Days.bounces)}</td>
                                    <td>{formatNumber(statistics.last30Days.bounces)}</td>
                                    <td>{formatNumber(statistics.allTime.bounces)}</td>
                                </tr>
                                <tr>
                                    <td>Complaints</td>
                                    <td>{formatNumber(statistics.last24Hours.complaints)}</td>
                                    <td>{formatNumber(statistics.last7Days.complaints)}</td>
                                    <td>{formatNumber(statistics.last30Days.complaints)}</td>
                                    <td>{formatNumber(statistics.allTime.complaints)}</td>
                                </tr>
                                <tr>
                                    <td>Rejects</td>
                                    <td>{formatNumber(statistics.last24Hours.rejects)}</td>
                                    <td>{formatNumber(statistics.last7Days.rejects)}</td>
                                    <td>{formatNumber(statistics.last30Days.rejects)}</td>
                                    <td>{formatNumber(statistics.allTime.rejects)}</td>
                                </tr>
                            </tbody>
                        </table>
                    </div>

                    <div className="rates-comparison">
                        <h2>Rate Comparison</h2>
                        <div className="rates-grid">
                            <div className="rate-card">
                                <h3>Delivery Rate</h3>
                                <div className="rate-values">
                                    <span>24h: {statistics.last24Hours.deliveryRate}%</span>
                                    <span>7d: {statistics.last7Days.deliveryRate}%</span>
                                    <span>30d: {statistics.last30Days.deliveryRate}%</span>
                                </div>
                            </div>
                            <div className="rate-card">
                                <h3>Open Rate</h3>
                                <div className="rate-values">
                                    <span>24h: {statistics.last24Hours.openRate}%</span>
                                    <span>7d: {statistics.last7Days.openRate}%</span>
                                    <span>30d: {statistics.last30Days.openRate}%</span>
                                </div>
                            </div>
                            <div className="rate-card">
                                <h3>Click Rate</h3>
                                <div className="rate-values">
                                    <span>24h: {statistics.last24Hours.clickRate}%</span>
                                    <span>7d: {statistics.last7Days.clickRate}%</span>
                                    <span>30d: {statistics.last30Days.clickRate}%</span>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Health Metrics Tab */}
            {activeTab === 'health' && currentMetrics && (
                <div className="tab-content">
                    <div className="health-grid">
                        <div className="health-card">
                            <h3>📧 Email Reputation</h3>
                            <div className="health-score">
                                <div className="score-circle score-excellent">
                                    <span className="score-number">A+</span>
                                </div>
                                <p>Excellent sender reputation</p>
                            </div>
                            <div className="health-details">
                                <div className="health-item">
                                    <span>Bounce Rate:</span>
                                    <strong className={currentMetrics.bounceRate < 2 ? 'text-success' : 'text-warning'}>
                                        {currentMetrics.bounceRate}%
                                    </strong>
                                </div>
                                <div className="health-item">
                                    <span>Complaint Rate:</span>
                                    <strong className={currentMetrics.complaintRate < 0.1 ? 'text-success' : 'text-warning'}>
                                        {currentMetrics.complaintRate}%
                                    </strong>
                                </div>
                            </div>
                        </div>

                        <div className="health-card">
                            <h3>🎯 Engagement Score</h3>
                            <div className="health-score">
                                <div className="score-circle score-good">
                                    <span className="score-number">{currentMetrics.openRate}%</span>
                                </div>
                                <p>Above industry average</p>
                            </div>
                            <div className="health-details">
                                <div className="health-item">
                                    <span>Open Rate:</span>
                                    <strong className="text-info">{currentMetrics.openRate}%</strong>
                                </div>
                                <div className="health-item">
                                    <span>Click Rate:</span>
                                    <strong className="text-info">{currentMetrics.clickRate}%</strong>
                                </div>
                            </div>
                        </div>

                        <div className="health-card">
                            <h3>✅ Deliverability</h3>
                            <div className="health-score">
                                <div className="score-circle score-excellent">
                                    <span className="score-number">{currentMetrics.deliveryRate}%</span>
                                </div>
                                <p>Optimal delivery rate</p>
                            </div>
                            <div className="health-details">
                                <div className="health-item">
                                    <span>Delivered:</span>
                                    <strong className="text-success">{formatNumber(currentMetrics.deliveries)}</strong>
                                </div>
                                <div className="health-item">
                                    <span>Failed:</span>
                                    <strong>{formatNumber(currentMetrics.bounces + currentMetrics.rejects)}</strong>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="recommendations-section">
                        <h2>💡 Recommendations</h2>
                        <div className="recommendations-list">
                            {currentMetrics.bounceRate > 2 && (
                                <div className="recommendation-item warning">
                                    <span className="rec-icon">⚠️</span>
                                    <div className="rec-content">
                                        <h4>Reduce Bounce Rate</h4>
                                        <p>Your bounce rate is above 2%. Consider implementing email validation and regularly cleaning your list.</p>
                                    </div>
                                </div>
                            )}
                            {currentMetrics.openRate < 20 && (
                                <div className="recommendation-item info">
                                    <span className="rec-icon">💡</span>
                                    <div className="rec-content">
                                        <h4>Improve Open Rates</h4>
                                        <p>Try A/B testing subject lines and optimizing send times for better engagement.</p>
                                    </div>
                                </div>
                            )}
                            {currentMetrics.openRate > 30 && currentMetrics.clickRate < 10 && (
                                <div className="recommendation-item info">
                                    <span className="rec-icon">🎯</span>
                                    <div className="rec-content">
                                        <h4>Boost Click-Through Rates</h4>
                                        <p>Great open rates! Now focus on improving CTAs and email content to drive more clicks.</p>
                                    </div>
                                </div>
                            )}
                            {currentMetrics.deliveryRate > 95 && currentMetrics.bounceRate < 2 && currentMetrics.complaintRate < 0.1 && (
                                <div className="recommendation-item success">
                                    <span className="rec-icon">✅</span>
                                    <div className="rec-content">
                                        <h4>Excellent Performance!</h4>
                                        <p>Your email metrics are excellent. Keep maintaining good list hygiene and content quality.</p>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            )}

            {/* Quota & Limits Tab */}
            {activeTab === 'quota' && quotaInfo && (
                <div className="tab-content">
                    <div className="quota-grid">
                        <div className="quota-card">
                            <h3>📦 24-Hour Sending Quota</h3>
                            <div className="quota-visual">
                                <div className="quota-bar-large">
                                    <div
                                        className="quota-fill"
                                        style={{ width: `${(quotaInfo.sentLast24Hours / quotaInfo.max24HourSend) * 100}%` }}
                                    >
                                        <span className="quota-text">
                                            {((quotaInfo.sentLast24Hours / quotaInfo.max24HourSend) * 100).toFixed(1)}% used
                                        </span>
                                    </div>
                                </div>
                                <div className="quota-numbers">
                                    <div className="quota-stat">
                                        <span>Sent:</span>
                                        <strong>{formatNumber(quotaInfo.sentLast24Hours)}</strong>
                                    </div>
                                    <div className="quota-stat">
                                        <span>Limit:</span>
                                        <strong>{formatNumber(quotaInfo.max24HourSend)}</strong>
                                    </div>
                                    <div className="quota-stat">
                                        <span>Remaining:</span>
                                        <strong className="text-success">{formatNumber(quotaInfo.remainingQuota)}</strong>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className="quota-card">
                            <h3>⚡ Sending Rate Limit</h3>
                            <div className="rate-limit-info">
                                <div className="rate-value">
                                    <span className="rate-number">{quotaInfo.maxSendRate}</span>
                                    <span className="rate-unit">emails/second</span>
                                </div>
                                <p className="rate-description">
                                    Maximum sustained sending rate allowed by AWS SES
                                </p>
                                <div className="rate-calculation">
                                    <div className="calc-item">
                                        <span>Per Minute:</span>
                                        <strong>{quotaInfo.maxSendRate * 60}</strong>
                                    </div>
                                    <div className="calc-item">
                                        <span>Per Hour:</span>
                                        <strong>{formatNumber(quotaInfo.maxSendRate * 3600)}</strong>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className="quota-card">
                            <h3>📊 Account Status</h3>
                            <div className="account-status">
                                <div className="status-item">
                                    <span className="status-label">SES Status:</span>
                                    <span className="status-badge badge-success">✅ Active</span>
                                </div>
                                <div className="status-item">
                                    <span className="status-label">Sandbox Mode:</span>
                                    <span className="status-badge badge-success">❌ Production</span>
                                </div>
                                <div className="status-item">
                                    <span className="status-label">Region:</span>
                                    <span className="status-badge badge-info">us-east-1</span>
                                </div>
                                <div className="status-item">
                                    <span className="status-label">Verified Domains:</span>
                                    <span className="status-badge badge-info">angelwatchedu.org</span>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="quota-info-section">
                        <h2>ℹ️ About SES Limits</h2>
                        <div className="info-cards">
                            <div className="info-card">
                                <h4>24-Hour Quota</h4>
                                <p>The maximum number of emails you can send in a 24-hour period. This resets every 24 hours.</p>
                            </div>
                            <div className="info-card">
                                <h4>Sending Rate</h4>
                                <p>The maximum number of emails you can send per second. AWS SES throttles requests that exceed this rate.</p>
                            </div>
                            <div className="info-card">
                                <h4>Increasing Limits</h4>
                                <p>Limits automatically increase as you build a good sending reputation. You can also request manual increases through AWS Support.</p>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default EmailMarketingDashboard;

